import React, { useCallback, useEffect } from 'react';
import styles from './Modal.module.css';
import Botao from '../Botao';
import Icon from '@/components/Icon';
import { mdiClose } from '@mdi/js';
import { Dialog } from '@base-ui/react/dialog';

/**
 * Contexto com a profundidade de aninhamento de Modals (0 = fora de qualquer modal).
 * Cada Modal lê sua própria profundidade para calcular um z-index exclusivo
 * (overlay/viewport acima de todo o modal pai, abaixo de qualquer modal filho)
 * e propaga depth+1 para seus descendentes, permitindo empilhamento correto
 * de modais aninhados. CampoSeletor e CampoMultiSeletor também usam isso para
 * elevar o z-index do seu Combobox.Positioner acima do popup do modal mais próximo.
 */
export const InsideModalContext = React.createContext(0);

const MODAL_Z_BASE = 1100;
const MODAL_Z_STEP = 300;

/**
 * z-index para elementos flutuantes (ex: Combobox.Positioner) que precisam
 * ficar acima do modal mais próximo. `depth` é o valor lido de InsideModalContext.
 */
export const getInsideModalZIndex = (depth: number) =>
  MODAL_Z_BASE - 100 + depth * MODAL_Z_STEP;

type ModalButtonProps = {
  visible?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  title?: string;
  href?: string;
  linkTarget?: string;
  variant?:
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-success'
  | 'outline-info'
  | 'outline-warning'
  | 'outline-danger';
};

type ModalProps = {
  closeable?: boolean;
  show: boolean;
  title?: React.ReactNode;
  children?: React.ReactNode;
  confirmProps?: ModalButtonProps;
  cancelProps?: ModalButtonProps;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  hideControls?: boolean;
  ignoreOverlayClick?: boolean;
  size?: 'sm' | 'md' | 'lg';
  instanceId?: string;
};

const defaultConfirmProps: ModalButtonProps = {
  visible: true,
  disabled: false,
  label: 'Confirmar',
};

const defaultCancelProps: ModalButtonProps = {
  visible: true,
  disabled: false,
  label: 'Cancelar',
};

const Modal: React.FC<ModalProps> = ({
  closeable = true,
  size = 'sm',
  show,
  title,
  children,
  confirmProps,
  cancelProps,
  hideControls,
  ignoreOverlayClick,
  instanceId,
  onClose,
  onConfirm,
  onCancel,
}) => {
  const [modalAttention, setModalAttention] = React.useState(false);

  const depth = React.useContext(InsideModalContext);
  const overlayZIndex = MODAL_Z_BASE + depth * MODAL_Z_STEP;
  const viewportZIndex = overlayZIndex + 100;

  // Mantém a classe 'modal-open' no body para compatibilidade
  useEffect(() => {
    if (show) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [show]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose],
  );

  // Quando ignoreOverlayClick=true, disablePointerDismissal já impede o fechamento.
  // Este handler apenas dispara a animação de atenção.
  const handleBackdropClick = () => {
    if (ignoreOverlayClick) {
      setModalAttention(true);
      setTimeout(() => setModalAttention(false), 200);
    }
  };

  const handleCancel = () => {
    onCancel ? onCancel() : onClose?.();
  };

  const handleConfirm = () => {
    onConfirm ? onConfirm() : onClose?.();
  };

  const confirmButtonProps = { ...defaultConfirmProps, ...confirmProps };
  const cancelButtonProps = { ...defaultCancelProps, ...cancelProps };

  return (
    <Dialog.Root
      open={show}
      onOpenChange={handleOpenChange}
      disablePointerDismissal={ignoreOverlayClick}
    >
      <InsideModalContext.Provider value={depth + 1}>
        <Dialog.Portal>
          <Dialog.Backdrop
            className={styles.overlay}
            style={{ zIndex: overlayZIndex }}
            onClick={handleBackdropClick}
            // Por padrão, o Base UI só renderiza o backdrop do Dialog mais externo
            // quando há modais aninhados (assumindo um único overlay compartilhado).
            // Forçamos um overlay por modal para que cada nível de aninhamento
            // tenha seu próprio overlay imediatamente atrás, bloqueando cliques
            // no modal pai enquanto o filho estiver aberto.
            forceRender
          />
          <Dialog.Viewport
            className={styles.viewport}
            style={{ zIndex: viewportZIndex }}
          >
            <Dialog.Popup
              id={instanceId}
              className={[
                styles.container,
                styles[`container-${size}`],
                modalAttention ? styles.modalAttention : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className={styles.titleContainer}>
                <Dialog.Title render={<div className={styles.title} />}>
                  {title}
                </Dialog.Title>
                {closeable && (
                  <div className={styles.closeContainer}>
                    <Botao variant="none-secondary" onClick={handleClose}>
                      <Icon
                        path={mdiClose}
                        size={1}
                        color="var(--tc-color-gray-500)"
                      />
                    </Botao>
                  </div>
                )}
              </div>
              <div className={styles.content}>{children}</div>
              {!hideControls && (
                <div className={styles.footer}>
                  {cancelButtonProps.visible && (
                    <Botao
                      disabled={cancelButtonProps.disabled}
                      variant={cancelButtonProps.variant || 'outline-secondary'}
                      onClick={handleCancel}
                      title={cancelButtonProps.title}
                    >
                      {cancelButtonProps.label}
                    </Botao>
                  )}
                  {confirmButtonProps.visible && (
                    <Botao
                      disabled={confirmButtonProps.disabled}
                      variant={confirmButtonProps.variant || 'primary'}
                      onClick={handleConfirm}
                      href={confirmButtonProps.href}
                      target={confirmButtonProps.linkTarget}
                      title={confirmButtonProps.title}
                    >
                      {confirmButtonProps.label}
                    </Botao>
                  )}
                </div>
              )}
            </Dialog.Popup>
          </Dialog.Viewport>
        </Dialog.Portal>
      </InsideModalContext.Provider>
    </Dialog.Root>
  );
};

export default Modal;
